// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type NoDataIconType = 'eyes' | 'no-signal' | undefined;

@Component({
  selector: 'lfx-no-data-chart',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="no-data">
      @if (iconType() !== undefined) {
        <div class="no-data-icon">
          @switch (iconType()) {
            @case ('eyes') {
              <svg width="76" height="53" viewBox="0 0 76 53" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M36.125 26.5C36.125 20.0547 34.1328 14.3125 31.0859 10.2109C28.0391 6.22656 24.1719 4 20.1875 4C16.0859 4 12.2188 6.22656 9.17188 10.2109C6.82812 13.4922 5.07031 17.7109 4.48438 22.6328C6.00781 21.5781 7.76562 20.9922 9.875 20.9922C15.0312 20.9922 19.25 25.0938 19.25 30.3672C19.25 35.5234 15.0312 39.7422 9.875 39.7422C8.82031 39.7422 7.88281 39.5078 7.0625 39.2734C7.64844 40.5625 8.35156 41.7344 9.17188 42.9062C12.2188 46.8906 16.0859 49 20.1875 49C24.1719 49 28.0391 46.8906 31.0859 42.9062C34.1328 38.8047 36.125 33.0625 36.125 26.5ZM38 15.3672C41.0469 6.46094 47.8438 0.25 55.8125 0.25C66.5938 0.25 75.5 12.0859 75.5 26.5C75.5 41.0312 66.5938 52.75 55.8125 52.75C47.8438 52.75 41.0469 46.6562 38 37.75C34.8359 46.6562 28.0391 52.75 20.1875 52.75C9.28906 52.75 0.5 41.0312 0.5 26.5C0.5 12.0859 9.28906 0.25 20.1875 0.25C28.0391 0.25 34.8359 6.46094 38 15.3672ZM44.7969 42.9062C47.8438 46.8906 51.7109 49 55.8125 49C59.7969 49 63.6641 46.8906 66.7109 42.9062C69.7578 38.8047 71.75 33.0625 71.75 26.5C71.75 20.0547 69.7578 14.3125 66.7109 10.2109C63.6641 6.22656 59.7969 4 55.8125 4C51.7109 4 47.8438 6.22656 44.7969 10.2109C42.4531 13.4922 40.6953 17.7109 40.1094 22.6328C41.6328 21.5781 43.3906 20.9922 45.5 20.9922C50.6562 20.9922 54.875 25.0938 54.875 30.3672C54.875 35.5234 50.6562 39.7422 45.5 39.7422C44.4453 39.7422 43.5078 39.5078 42.6875 39.2734C43.2734 40.5625 43.9766 41.7344 44.7969 42.9062ZM4.25 30.25C4.25 32.3594 5.30469 34.1172 7.0625 35.1719C8.70312 36.2266 10.9297 36.2266 12.6875 35.1719C14.3281 34.1172 15.5 32.3594 15.5 30.25C15.5 28.2578 14.3281 26.5 12.6875 25.4453C10.9297 24.3906 8.70312 24.3906 7.0625 25.4453C5.30469 26.5 4.25 28.2578 4.25 30.25ZM45.5 24.625C43.3906 24.625 41.6328 25.7969 40.5781 27.4375C39.5234 29.1953 39.5234 31.4219 40.5781 33.0625C41.6328 34.8203 43.3906 35.875 45.5 35.875C47.4922 35.875 49.25 34.8203 50.3047 33.0625C51.3594 31.4219 51.3594 29.1953 50.3047 27.4375C49.25 25.7969 47.4922 24.625 45.5 24.625Z" fill="var(--lf-color-neutral-gray)"/>
              </svg>
            }
            @case ('no-signal') {
              <i class="fas fa-signal-alt-slash"></i>
            }
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .no-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: var(--lf-tech-chart-height);
      gap: var(--lf-spacing-12);
      background: var(--lf-color-bg-primary);

      .no-data-icon {
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 76px;
          height: 53px;
        }

        i {
          font-size: 60px;
          color: var(--lf-color-neutral-gray);
        }
      }

      .no-data-content {
        display: flex;
        flex-direction: column;
        gap: var(--lf-spacing-12);
        text-align: center;

        .no-data-title {
          font-size: var(--lf-font-size-xs);
          font-weight: var(--lf-font-weight-semibold);
          color: var(--lf-tech-no-data-text-color);
          margin: 0;
          line-height: 1.4;
        }

        .no-data-text {
          font-size: var(--lf-font-size-xs);
          color: var(--lf-tech-no-data-text-color);
          line-height: 1.4;
          margin: 0;
          max-width: 200px;
        }
      }
    }
  `]
})
export class NoDataChartComponent {
  // Component identifier for accessibility and testing
  readonly componentId = 'no-data-chart';

  // Input property to configure the icon type
  // Default to undefined - no icon shown unless explicitly set
  readonly iconType = input<NoDataIconType>(undefined);
}
